import { Guid, IDictionary, ITreeNode, Icons, isNullOrEmpty } from "@ioip/core/lib";
import { TreeNode } from "@ioip/core/lib/types/TreeNode";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { batch, useDispatch, useSelector } from "react-redux";
import { IEntitySchema } from "../../../data";
import { Form } from "../../../forms/types";
import { IExtendedProjectSchema, CProject } from "../../../projects";
import { ControlType } from "../../../types";
import { IReduxProcesoxState } from "../../rtk/IReduxProcesoxState";
import { IObject, IWizardPageState } from "../../types";
import { IFormSchema } from "../../../forms/types/IFormSchema";
import { IFieldSchema, defaultIFieldSchema } from "../../../forms/types/IFieldSchema";

let _project: CProject;

export interface IFormEditorState {
    project?: IExtendedProjectSchema;
    forms: IDictionary<IFormSchema>; // Store the form schema for form editor.
    currentFormField: IDictionary<string>; // The ID of the current field for each form.
    currentFormId?: string; // The ID of the selected field, to show the properties.
    currentFlowNodeId?: string; // The ID of the selected flow node, to show the properties.
    wizardPageData: IDictionary<IWizardPageState<any>>;
    tabPageState: IDictionary<any>;
}

const initialState: IFormEditorState = {
    forms: {},
    currentFormField: {},
    wizardPageData: {},
    tabPageState: {}
};

/**
 * Redux slice for managing form editor state.
 * @name formEditorSlice
 */
export const formEditorSlice = createSlice({
    name: 'formEditor',
    initialState: initialState,
    reducers: {

        setFormSchema: (state: IFormEditorState, action: PayloadAction<{ formId: string, schema: IFormSchema }>) => {
            const { formId, schema } = action.payload;
            state.forms[ formId ] = schema;
        },

        addFormField: (state: IFormEditorState, action: PayloadAction<{ formId: string, schema: Partial<IFieldSchema> }>) => {
            const { formId, schema } = action.payload;
            state.forms[ formId ] = {
                ...state.forms[ formId ],
                fields: [
                    ...state.forms[ formId ].fields,
                    {
                        ...defaultIFieldSchema(schema.controlType ?? ControlType.textField),
                        ...schema
                    }
                ]
            }
        },

        setPartialForm: (state: IFormEditorState, action: PayloadAction<{ formId: string, schema: Partial<IFormSchema> }>) => {
            const { formId, schema } = action.payload;
            state.forms[ formId ] = {
                ...state.forms[ formId ],
                ...schema
            }
        },

        /**
         * 
         * @param state 
         * @param action 
         */
        removeField: (state: IFormEditorState, action: PayloadAction<{ formId: string, fieldId: string }>) => {
            const { formId, fieldId } = action.payload;
            state.forms[ formId ] = {
                ...state.forms[ formId ],
                fields: state.forms[ formId ].fields.filter(x => x.id != fieldId)
            }
        },

        setPartialFieldSchema: (state: IFormEditorState, action: PayloadAction<{ formId: string, fieldId: string, schema: Partial<IFieldSchema> }>) => {
            const { formId, fieldId, schema } = action.payload;
            state.forms[ formId ] = {
                ...state.forms[ formId ],
                fields: state.forms[ formId ].fields.map(x => x.id == fieldId ? { ...x, ...schema } : x)
            }
        },

        setSelectedFieldId: (state: IFormEditorState, action: PayloadAction<{ formId: string, fieldId: string }>) => {
            const { formId, fieldId } = action.payload;
            state.currentFormField[ formId ] = fieldId;
        },

        setProject: (state: IFormEditorState, action: PayloadAction<IExtendedProjectSchema>) => {
            state.project = action.payload;
            _project = new CProject({ schema: action.payload });
        },

        setCurrentFormId: (state: IFormEditorState, action: PayloadAction<string>) => {
            state.currentFormId = action.payload;
        },

        setCurrentFlowNodeId: (state: IFormEditorState, action: PayloadAction<string>) => {
            state.currentFlowNodeId = action.payload;
        },

        setWizardPageState: (state: IFormEditorState, action: PayloadAction<{ tabId: string, schema: Partial<any> }>): void => {
            const { tabId, schema } = action.payload;
            state.wizardPageData[ tabId ] = {
                ...state.wizardPageData[ tabId ],
                ...schema
            }
        },

        removeWizardPage: (state: IFormEditorState, action: PayloadAction<string>): void => {
            const tabId = action.payload;
            delete state.wizardPageData[ tabId ];
        },

        setTabPageState: (state: IFormEditorState, action: PayloadAction<{ tabId: string, data: any }>): void => {
            const { tabId, data } = action.payload;
            state.tabPageState[ tabId ] = data;
        },

        removeTabState: (state: IFormEditorState, action: PayloadAction<string>): void => {
            const tabId = action.payload;
            delete state.tabPageState[ tabId ];
        },

        removeForm: (state: IFormEditorState, action: PayloadAction<string>): void => {
            const formId = action.payload;
            delete state.forms[ formId ];
        }

    }
});

/**
 * Returns an object with functions and data related to the form editor.
 * @returns An object with functions and data related to the form editor.
 */
export const useProcesoxEditor = () => {

    const dispatch = useDispatch();
    const {
        currentFormField,
        currentFormId,
        forms,
        tabPageState: pageState,
        project,
        wizardPageData,
        currentFlowNodeId
    } = useSelector<IReduxProcesoxState, IFormEditorState>(state => state.formEditor);

    /**
     * Returns the entity schema for a given form ID.
     * @param formId - The ID of the form.
     * @returns The entity schema object.
     */
    const getFormEntity = (formId: string): IObject<IEntitySchema> => {
        const form = forms[ formId ];
        if (form == null || form.entityId == null)
            return null;
        return project.entities?.find(x => x.objectId == form.entityId);
    }

    const setProject = (project: IExtendedProjectSchema) => {
        dispatch(formEditorSlice.actions.setProject(project));
    }

    const getRootNode = (options: {
        onEditForm: (form: IObject<IFormSchema>) => void;
        onEditEntity: (entity: IObject<IEntitySchema>) => void;
    }): TreeNode => {

        if (project == null)
            return null;

        const nodes: ITreeNode[] = [
            {
                id: 'root',
                text: project.name,
                icon: Icons.Folder,
            },
            {
                id: 'entities',
                text: 'Entities',
                icon: Icons.Folder,
                parentId: 'root'
            },
            {
                id: 'forms',
                text: 'Forms',
                icon: Icons.Folder,
                parentId: 'root'
            },
            {
                id: 'processes',
                text: 'Processes',
                icon: Icons.Folder,
                parentId: 'root'
            }
        ];

        // Create entity nodes
        project.entities.slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach(entity => {
                nodes.push({
                    id: entity.objectId,
                    text: entity.name,
                    icon: Icons.Table,
                    parentId: 'entities',
                    onDoubleClick: () => options.onEditEntity?.(entity)
                });
            });

        // Create form nodes
        project.forms.slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach(form => {
                nodes.push({
                    id: form.objectId,
                    text: form.name,
                    icon: Icons.Form,
                    parentId: 'forms',
                    onDoubleClick: () => options.onEditForm?.(form)
                });
            });

        // Create root node
        const root = TreeNode.createRootNode(nodes);
        return root;

    }

    const setFormSchema = (formId: string, schema: IFormSchema) => {
        dispatch(formEditorSlice.actions.setFormSchema({ formId, schema }));
    };

    const addField = (formId: string, schema: Partial<IFieldSchema>) => {
        dispatch(formEditorSlice.actions.addFormField({ formId, schema }));
    }

    const removeField = (formId: string, fieldId: string) => {
        batch(() => {
            dispatch(formEditorSlice.actions.removeField({ formId, fieldId }));
        });
    }

    const getGlobalSelectedField = (): IFieldSchema => {
        const { currentFormId, currentFormField } = useSelector<IReduxProcesoxState, IFormEditorState>(state => state.formEditor);
        if (isNullOrEmpty(currentFormId))
            return null;
        return getForm(currentFormId)?.schema.fields.find(x => x.id == currentFormField[ currentFormId ]);
    };

    const getSelectedFieldId = (formId: string): string => {
        const { currentFormField } = useSelector<IReduxProcesoxState, IFormEditorState>(state => state.formEditor);
        return currentFormField[ formId ];
    };

    const setCurrentFormId = (formId: string) => {
        dispatch(formEditorSlice.actions.setCurrentFormId(formId));
    }

    const getForm = (formId: string) => {
        return {
            schema: forms[ formId ],
            addField: (schema: Partial<IFieldSchema>) => {
                dispatch(formEditorSlice.actions.addFormField({ formId, schema }));
            },
            setPartial: (schema: Partial<IFormSchema>) => {
                dispatch(formEditorSlice.actions.setPartialForm({ formId, schema }));
            },
            getEntity: () => {
                return getFormEntity(formId);
            },
            getForm: (): Form => {
                return _project.getForm(Guid.parse(formId));
            }
        };
    }

    const removeForm = (formId: string) => {
        dispatch(formEditorSlice.actions.removeForm(formId));
    }

    const setPartialField = (formId: string, fieldId: string, schema: Partial<IFieldSchema>) => {
        dispatch(formEditorSlice.actions.setPartialFieldSchema({ formId, fieldId, schema }));
    }

    const setGlobalPartialField = (schema: Partial<IFieldSchema>) => {
        const currentFieldId = currentFormField[ currentFormId ]
        setPartialField(currentFormId, currentFieldId, schema);
    }

    const selectField = (formId: string, fieldId: string) => {
        batch(() => {
            dispatch(formEditorSlice.actions.setSelectedFieldId({ formId, fieldId }));
            dispatch(formEditorSlice.actions.setCurrentFormId(formId));
        });
    }

    const setPartialForm = (formId: string, schema: IFormSchema) => {
        dispatch(formEditorSlice.actions.setFormSchema({ formId, schema }));
    }

    const addRow = (formId: string) => {

        // Get max row order
        const maxRowOrder = Math.max(...forms[ formId ].fields.filter(x => x.controlType == ControlType.row).map(x => x.order)) ?? 0;

        dispatch(formEditorSlice.actions.addFormField({
            formId,
            schema: {
                ...defaultIFieldSchema(ControlType.row),
                order: maxRowOrder + 1
            }
        }));

    }

    const insertRowBefore = (formId: string, referenceFieldId: string) => {

    };

    const insertRowAfter = (formId: string, referenceFieldId: string) => {

        // Get fields with the same order or greater
        const fields = forms[ formId ].fields;
        const referenceIndex = fields.findIndex(x => x.id == referenceFieldId);
        const referenceOrder = fields[ referenceIndex ].order;

    };

    const updateRowOrder = (formId: string, fieldId: string, order: number) => {

        // Get fields with the same order or greater
        const fields = forms[ formId ].fields.filter(x => x.order >= order);

        batch(() => {

            // Update order
            fields.forEach(field => {
                dispatch(formEditorSlice.actions.setPartialFieldSchema({ formId, fieldId: field.id, schema: { order: field.order + 1 } }));
            });

            // Update order of the field
            dispatch(formEditorSlice.actions.setPartialFieldSchema({ formId, fieldId, schema: { order } }));

        });

    }

    /**
     * 
     * @param tabId 
     * @param initialState 
     * @returns 
     */
    const useWizardPage = function useWizardPage<T>(tabId: string) {

        const state: IWizardPageState<T> = wizardPageData[ tabId ];

        return {
            state,
            errors: state?.errors ?? {},
            data: state?.data,

            currentStep: state?.currentStep,
            setCurrentStep: (currentStep: string) => dispatch(formEditorSlice.actions.setWizardPageState({ tabId, schema: { currentStep } })),

            setState: (state: IWizardPageState<T>) => dispatch(formEditorSlice.actions.setWizardPageState({ tabId, schema: state })),
            setPartialState: (partial: Partial<IWizardPageState<T>>) => dispatch(formEditorSlice.actions.setWizardPageState({ tabId, schema: partial })),

            setData: (values: T) => dispatch(formEditorSlice.actions.setWizardPageState({ tabId, schema: { data: values } })),
            setPartialData: (partial: Partial<T>) => dispatch(formEditorSlice.actions.setWizardPageState({ tabId, schema: { data: { ...state?.data, ...partial } } })),

            setErrors: (values: IDictionary<string>) => dispatch(formEditorSlice.actions.setWizardPageState({ tabId, schema: { errors: values } })),
            setPartialErrors: (partial: IDictionary<string>) => dispatch(formEditorSlice.actions.setWizardPageState({ tabId, schema: { errors: { ...state?.errors, ...partial } } })),

        }

    }

    const setWizardPageData = function setWizardPageData<T>(tabId: string, schema: Partial<IWizardPageState<T>>) {
        dispatch(formEditorSlice.actions.setWizardPageState({ tabId, schema }));
    }

    const removeWizardPage = function removeWizardPage(tabId: string) {
        dispatch(formEditorSlice.actions.removeWizardPage(tabId));
    };

    const getProject = (): CProject => {
        return new CProject({ schema: project });
    }

    const getCurrentForm = () => {
        return getForm(currentFormId);
    }

    const useTabState = function setTabState<T>(tabId: string, initialState?: T) {
        const state = pageState[ tabId ];
        return {
            isMounted: state != null,
            state: state as T ?? initialState,
            setState: (state: T) => {
                dispatch(formEditorSlice.actions.setTabPageState({
                    tabId,
                    data: state
                }));
            },
            setPartialState: (partial: Partial<T>) => {
                dispatch(formEditorSlice.actions.setTabPageState({
                    tabId,
                    data: {
                        ...state,
                        ...partial
                    }
                }));
            }
        }

    };

    const removeTabState = (tabId: string) => {
        dispatch(formEditorSlice.actions.removeTabState(tabId));
    };

    const flowNode = {
        setCurrentFlowNodeId: (nodeId: string) => {
            dispatch(formEditorSlice.actions.setCurrentFlowNodeId(nodeId));
        },
        getCurrentFlowNodeId: () => {
            return currentFlowNodeId;
        }
    }

    return {
        flowNode,
        addField,
        addRow,
        currentFormId,
        forms,
        getCurrentForm,
        getForm,
        getFormEntity,
        getGlobalSelectedField,
        getRootNode,
        getSelectedFieldId,
        insertRowAfter,
        insertRowBefore,
        project,
        Project: _project,
        removeField,
        removeForm,
        removeTabState,
        removeWizardPage,
        selectField,
        setCurrentFormId,
        setFormSchema,
        setGlobalPartialField,
        setPartialField,
        setPartialForm,
        setProject,
        setWizardPageData,
        updateRowOrder,
        useTabState,
        useWizardPage
    }

}